/*
 * ContactForm.tsx : Un formulaire pour afficher un contact de manière compacte.
 */

// Import React
import React from 'react';
import { Box } from '@radix-ui/themes';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faUserTie,
	faPhone,
	faMobileAlt,
	faEnvelope,
	faTrash,
	faBuilding,
} from '@fortawesome/free-solid-svg-icons';

interface ContactCardProps {
	nom: string;
	prenom: string;
	entreprise?: string;
	telPro?: string;
	telMobile?: string;
	email?: string;
	poste?: string;
	chargeDAffaire?: boolean;
	onEdit?: () => void;
	onDelete?: () => void;
}

export function ContactCard({
	nom,
	prenom,
	entreprise,
	telPro,
	telMobile,
	email,
	poste,
	chargeDAffaire = false,
	onEdit,
	onDelete,
}: ContactCardProps) {
	return (
		<Box
			className={`border rounded-lg pt-3 pb-3 px-3 flex flex-col gap-1 w-full shadow-md hover:shadow-lg transition-shadow duration-150
    ${chargeDAffaire ? 'bg-blue-100' : 'bg-white'} relative cursor-pointer`}
			onClick={() => onEdit && onEdit()}
		>
			{/* Header avec icône, nom/poste et badge */}
			<div className="flex items-center gap-2 mt-1">
				{/* Badge chargé d'affaire en haut à droite */}
				{chargeDAffaire && (
					<div className="absolute top-2 right-2 px-2 py-[1px] bg-blue-500 text-white font-semibold text-[10px] rounded-full shadow-md">
						Chargé d&apos;affaire
					</div>
				)}

				{/* Icône + nom et poste */}
				<div className="flex items-center gap-2 mt-1">
					<FontAwesomeIcon
						icon={faUserTie}
						className="text-2xl text-gray-700"
					/>
					<div className="flex flex-col">
						<span className="font-bold text-sm">
							{prenom} {nom}
						</span>
						<span className="text-gray-500 text-xs">{poste ? poste : '—'}</span>
					</div>
				</div>

				{/* Actions supprimer */}
				<div className="absolute bottom-2 right-2 flex items-center gap-2 text-gray-400">
					{onDelete && (
						<button
							onClick={(e) => {
								e.stopPropagation(); // empêche le clic card
								onDelete();
							}}
							className="hover:text-red-600"
						>
							<FontAwesomeIcon icon={faTrash} />
						</button>
					)}
				</div>
			</div>

			{/* Infos contact avec icônes */}
			<div className="text-xs flex flex-col gap-1 mt-1">
				{entreprise && (
					<span className="flex items-center gap-1 text-gray-600">
						<FontAwesomeIcon icon={faBuilding} className="text-gray-400" />
						{entreprise}
					</span>
				)}
				{telPro && (
					<span className="flex items-center gap-1 text-gray-600">
						<FontAwesomeIcon icon={faPhone} className="text-gray-400" />
						{telPro}
					</span>
				)}
				{telMobile && (
					<span className="flex items-center gap-1 text-gray-600">
						<FontAwesomeIcon icon={faMobileAlt} className="text-gray-400" />
						{telMobile}
					</span>
				)}
				{email && (
					<span className="flex items-center gap-1 text-gray-600">
						<FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
						{email}
					</span>
				)}
			</div>
		</Box>
	);
}
